import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { enqueueSnackbar } from "notistack";

export interface FeatureFlag {
  id: number;
  created_at: string;
  name: string;
  display_name: string;
  desc: string | null;
  enabled: boolean;
}

const QUERY_KEY = ["supabase-feature-flags"] as const;

export function useSupabaseFeatureFlags() {
  const queryClient = useQueryClient();

  // Fetch feature flags
  const {
    data: flags = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        throw error;
      }

      return data as FeatureFlag[];
    },
  });

  // Toggle flag mutation
  const toggleFlagMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: number; enabled: boolean }) => {
      const { error } = await supabase.from("feature_flags").update({ enabled }).eq("id", id);

      if (error) {
        throw error;
      }

      return { id, enabled };
    },
    onSuccess: (_, { enabled }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      enqueueSnackbar(`Feature flag ${enabled ? "enabled" : "disabled"}`, {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar("Couldn't update feature flag. Please try again.", {
        variant: "error",
      });
    },
  });

  return {
    flags,
    isLoading,
    error,
    toggleFlag: toggleFlagMutation.mutate,
    isToggling: toggleFlagMutation.isPending,
  };
}
